import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import { COLLECTIONS } from '../platform/constants';
import { LAST_READ_MODULES } from '../platform/lastRead/lastReadModules';
import { markItemAsRead } from '../platform/lastRead/lastReadService';
import { syncAttachmentFields } from '../platform/contentAttachments';
import {
  deleteContentAttachmentStorage,
  uploadFilesAsAttachments,
} from './attachmentUploadService';
import { withTenantScope } from './contentWriteFields';
import { uploadFileToStorage } from './storageUpload';
import {
  ARTICLE_IMPORTANCE,
  MAX_ESSENTIAL_ARTICLES,
  normalizeArticleRecord,
} from './articleListGroups';
import {
  applyTenantScopeToCollection,
  buildCreatedAtListQuery,
  mapAndFilterSnapshot,
} from './tenantListQuery';
import { filterByActiveTenant } from './tenantContentFilter';

export { ARTICLE_IMPORTANCE, MAX_ESSENTIAL_ARTICLES };

export const ARTICLES_PAGE_SIZE = 20;
export const ARTICLES_LIST_LIMIT = 200;

function articlesCollection() {
  return collection(db, COLLECTIONS.ARTICLES);
}

function mapArticle(docSnap) {
  const data = docSnap.data();
  return normalizeArticleRecord({
    id: docSnap.id,
    ...data,
    createdAt: data.createdAt?.toDate?.() ?? null,
  });
}

export function mapArticleFromFirestore(docSnap) {
  return mapArticle(docSnap);
}

function buildArticlesListQuery(user, {
  tenantId,
  memberships = [],
  pageSize = ARTICLES_PAGE_SIZE,
  lastVisible = null,
} = {}) {
  return buildCreatedAtListQuery({
    collectionName: COLLECTIONS.ARTICLES,
    tenantId,
    user,
    memberships,
    pageSize,
    lastVisible,
  });
}

export function buildArticlesPageQuery(user, tenantId, memberships, options = {}) {
  return buildArticlesListQuery(user, {
    tenantId,
    memberships,
    pageSize: options.pageSize ?? ARTICLES_PAGE_SIZE,
    lastVisible: options.startAfterDoc ?? null,
  });
}

export function buildArticlesGroupedListQuery(user, tenantId, memberships) {
  return buildArticlesListQuery(user, {
    tenantId,
    memberships,
    pageSize: ARTICLES_LIST_LIMIT,
    lastVisible: null,
  });
}

export async function fetchArticlesPage(user, tenantId, memberships, options = {}) {
  const listQuery = buildArticlesPageQuery(user, tenantId, memberships, options);
  const querySnapshot = await getDocs(listQuery);
  return {
    articles: mapAndFilterSnapshot(querySnapshot, tenantId, mapArticle),
    lastVisible: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
  };
}

export async function countEssentialArticles(tenantId, excludeArticleId = null) {
  if (!tenantId) {
    return 0;
  }

  const scoped = applyTenantScopeToCollection(COLLECTIONS.ARTICLES, tenantId);
  const essentialQuery = query(
    scoped,
    where('importance', '==', ARTICLE_IMPORTANCE.ESSENTIAL),
  );
  const querySnapshot = await getDocs(essentialQuery);

  return filterByActiveTenant(
    querySnapshot.docs.map(mapArticle),
    tenantId,
  ).filter((article) => article.id !== excludeArticleId).length;
}

export async function assertCanMarkArticleEssential(tenantId, excludeArticleId = null) {
  const activeCount = await countEssentialArticles(tenantId, excludeArticleId);
  if (activeCount >= MAX_ESSENTIAL_ARTICLES) {
    throw new Error(
      `Naenkrat je lahko označenih največ ${MAX_ESSENTIAL_ARTICLES} obrazcev kot «Aktualno.`,
    );
  }
}

export async function createArticle(user, {
  activeTenantId,
  title,
  description,
  files = [],
  thumbnail,
  unit,
  unitName,
  listGroupId = 'other',
  importance = ARTICLE_IMPORTANCE.NORMAL,
  onProgress,
}) {
  let attachmentFields = syncAttachmentFields([]);
  if (files?.length) {
    const uploaded = await uploadFilesAsAttachments(files, 'articles', {
      onProgress,
      tenantId: activeTenantId,
    });
    attachmentFields = syncAttachmentFields(uploaded);
  }

  let thumbnailUrl = '';
  if (thumbnail) {
    thumbnailUrl = await uploadFileToStorage(thumbnail, 'article-thumbnails', {
      onProgress,
      tenantId: activeTenantId,
    });
  }

  if (importance === ARTICLE_IMPORTANCE.ESSENTIAL) {
    await assertCanMarkArticleEssential(activeTenantId);
  }

  await addDoc(
    articlesCollection(),
    withTenantScope(activeTenantId, {
      title,
      description,
      ...attachmentFields,
      uid: user.uid,
      displayName: user.displayName || 'Anonimen',
      createdAt: serverTimestamp(),
      unit,
      unitName,
      thumbnailUrl,
      listGroupId,
      importance,
      order: 0,
    }),
  );
}

export async function updateArticle(articleId, payload) {
  const nextPayload = { ...payload };
  if (payload.attachments) {
    Object.assign(nextPayload, syncAttachmentFields(payload.attachments));
  }
  await updateDoc(doc(db, COLLECTIONS.ARTICLES, articleId), nextPayload);
}

export async function updateArticleGrouping(
  articleId,
  { listGroupId, importance },
  tenantId,
  { currentImportance = null } = {},
) {
  const normalizedImportance = importance || ARTICLE_IMPORTANCE.NORMAL;

  if (
    normalizedImportance === ARTICLE_IMPORTANCE.ESSENTIAL
    && tenantId
    && currentImportance !== ARTICLE_IMPORTANCE.ESSENTIAL
  ) {
    await assertCanMarkArticleEssential(tenantId, articleId);
  }

  const payload = {
    importance: normalizedImportance,
    listGroupId: normalizedImportance === ARTICLE_IMPORTANCE.ARCHIVE
      ? 'archive'
      : (listGroupId || 'other'),
  };
  await updateDoc(doc(db, COLLECTIONS.ARTICLES, articleId), payload);
}

export async function deleteArticle(article) {
  try {
    await deleteContentAttachmentStorage(article, {
      extraUrls: [article.thumbnailUrl],
    });
  } catch (error) {
    if (error.code !== 'not-found' && !error.message?.includes('object-not-found')) {
      throw error;
    }
  }
  await deleteDoc(doc(db, COLLECTIONS.ARTICLES, article.id));
}

export async function markArticleAsRead(userId, articleId) {
  await markItemAsRead(userId, LAST_READ_MODULES.ARTICLES, articleId);
}
